import { useEffect } from 'react';
import {
  startHandoffSignalListener,
  type HandoffSignalDoc,
} from '../lib/services/firebase/conversationListener';
import { useGlobalStore } from '../stores/globalStore';
import type { ConversationListenerResult } from '../lib/services/firebase/conversationListener';
import { useHandoffSummaryStore } from '../stores/handoffSummaryStore';

/** The fallback poll: anything the signal and the focus refresh both missed shows up within this. */
export const HANDOFF_SUMMARY_POLL_MS = 60_000;

/**
 * A burst of notification docs (a shopper typing three lines, each written as its own doc) is one
 * refresh, not three. Short enough that the merchant cannot tell the dot did not light instantly.
 */
export const HANDOFF_SIGNAL_DEBOUNCE_MS = 800;

/**
 * Keeps the handoff red-dot count fresh (CS 2.0 需求 13: 「新会话进来或状态变化时实时更新，不需刷新页面」).
 *
 * <p><b>Mount it once</b>, at the layout: it owns WHEN the count is re-read, and two of it would read
 * twice. Every surface that shows the number reads `useHandoffSummary` instead.
 *
 * <p>Three triggers, because none of them alone is enough:
 * <ul>
 *   <li><b>The Firestore signal.</b> chatbot-api writes a doc to
 *       `users/{id}/externalActivationNotifications` for each shopper message to a handed-off
 *       conversation, and a `handoff_changed` doc when a mark is set, escalated or withdrawn. Any
 *       new doc re-reads the count. This listener only signals — it never touches the conversation
 *       store, which Anna Chat's own listener drives (see `startHandoffSignalListener`).</li>
 *   <li><b>Focus.</b> Coming back to the tab is the moment the merchant looks.</li>
 *   <li><b>A 60-second poll</b>, for whatever the other two miss: a merchant whose account has no
 *       Firestore read rule, a doc written while the socket was down.</li>
 * </ul>
 * The merchant's own reply and "Hand back to Anna" refresh from where they happen
 * (`refreshHandoffSummary`), so those never wait for any of the three.
 *
 * @param userId the merchant, or undefined when there is nothing to count (signed out, or a plan
 *     without Anna) — the count is then cleared and nothing is fetched.
 */
export function useHandoffSummarySource(userId: string | undefined): void {
  useEffect(() => {
    const { refresh, reset } = useHandoffSummaryStore.getState();
    if (!userId) {
      reset();
      return;
    }

    let cancelled = false;
    let listener: ConversationListenerResult | null = null;
    let debounce: ReturnType<typeof setTimeout> | null = null;

    const pull = () => {
      if (!cancelled) void refresh(userId);
    };
    const pullSoon = () => {
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(pull, HANDOFF_SIGNAL_DEBOUNCE_MS);
    };

    pull();
    const poll = setInterval(pull, HANDOFF_SUMMARY_POLL_MS);
    const onFocus = () => pull();
    const onVisible = () => {
      if (document.visibilityState === 'visible') pull();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);

    // Same late-resolve guard as AnnaChatPage's listener: under StrictMode's setup → cleanup → setup
    // the first promise can resolve after its cleanup ran, leaving a listener nothing can cancel.
    // 需求 10 ④「平台内即时通知」: a conversation that just turned urgent pops a notice, once. The
    // listener replays the last few minutes on start — only what arrives after mount is news.
    const mountedAt = Date.now();
    const onSignal = (added: HandoffSignalDoc[] = []) => {
      pullSoon();
      const urgent = added.filter(
        d =>
          d.type === 'handoff_changed' &&
          d.status === 'urgent' &&
          Number(d.timestamp ?? 0) >= mountedAt
      );
      if (urgent.length > 0) {
        useGlobalStore.getState().addNotification({
          type: 'warning',
          message:
            urgent.length === 1
              ? 'An urgent conversation is waiting for your team in Anna Chat.'
              : `${urgent.length} urgent conversations are waiting for your team in Anna Chat.`,
        });
      }
    };
    startHandoffSignalListener(userId, onSignal)
      .then(handle => {
        if (cancelled) handle.unsubscribe();
        else listener = handle;
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      clearInterval(poll);
      if (debounce) clearTimeout(debounce);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
      listener?.unsubscribe();
      listener = null;
    };
  }, [userId]);
}
